<template>
  <div class="month-view-container">
    <!-- 顶部工具栏 -->
    <div class="toolbar">
      <div class="toolbar-left">
        <span class="toolbar-title">全员档期</span>
        <div class="year-picker">
          <div class="year-picker-btn" @click="changeYear(-1)">
            <i class="el-icon-arrow-left"></i>
          </div>
          <span class="year-value">{{ currentYear }}</span>
          <div class="year-picker-btn" @click="changeYear(1)">
            <i class="el-icon-arrow-right"></i>
          </div>
        </div>
        <div class="month-picker">
          <el-select v-model="currentMonth" placeholder="请选择月份" size="small" @change="handleMonthChange">
            <el-option v-for="month in 12" :key="month" :label="month + '月'" :value="month">
            </el-option>
          </el-select>
        </div>
      </div>
      <div class="toolbar-right">
        <div class="toolbar-legend" v-if="scheduleTypeList.length">
          <span v-for="type in scheduleTypeList" :key="type.atstId" class="legend-item" :title="type.atstName">
            <span class="legend-color-block" :style="{ backgroundColor: type.atstColor }"></span>
            <span class="legend-text">{{ type.atstName }}</span>
          </span>
        </div>
      </div>
    </div>

    <!-- 月历视图 -->
    <div class="calendar-view" v-loading="loading">
      <!-- 星期表头 -->
      <div class="week-header">
        <div v-for="week in weekDays" :key="week" class="week-cell" :class="{ 'is-weekend': week === '日' || week === '六' }">
          {{ week }}
        </div>
      </div>

      <!-- 日期格子 -->
      <div class="calendar-grid">
        <div
          v-for="(day, index) in calendarCells"
          :key="index"
          class="calendar-cell"
          :class="{
            'is-empty': !day.day,
            'is-weekend': day.isWeekend,
            'is-today': day.isToday,
            'is-other-month': day.isOtherMonth
          }"
        >
          <div v-if="day.day" class="cell-content">
            <span class="day-number">{{ day.day }}</span>
            <div class="schedule-tags">
              <span
                v-for="schedule in day.schedules"
                :key="schedule.atsId"
                class="schedule-tag"
                :style="{ backgroundColor: schedule.displayColor }"
                :title="schedule.displayText"
                @click.stop="handleScheduleClick(schedule)"
              >
                {{ schedule.displayText }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 日程详情弹窗 -->
    <el-dialog title="日程详情" :visible.sync="dialogVisible" width="50%" append-to-body class="schedule-dialog">
      <div class="dialog-content">
        <div class="dialog-header-mini">
          <div class="lecturer-avatar-mini">{{ getAvatarText(selectedSchedule?.teacherName) }}</div>
          <div class="lecturer-info-mini">
            <span class="lecturer-name-mini">{{ selectedSchedule?.teacherName }}</span>
            <span class="schedule-type-tag" :style="{ backgroundColor: selectedSchedule?.displayColor }">
              {{ selectedSchedule?.atstName }}
            </span>
          </div>
        </div>
        <el-form label-width="80px" size="small" class="detail-form">
          <el-form-item label="客户名称">
            <span class="detail-value">{{ selectedSchedule?.trainCustomerName }}</span>
          </el-form-item>
          <el-form-item label="课程名称">
            <span class="detail-value">{{ selectedSchedule?.trainCourseName }}</span>
          </el-form-item>
          <el-form-item label="培训时长">
            <span class="detail-value">{{ selectedSchedule?.trainTimeLength }}天</span>
          </el-form-item>
          <el-form-item label="培训形式">
            <span class="detail-value">{{ selectedSchedule?.trainForm === 1 ? '线下' : '线上' }}</span>
          </el-form-item>
          <el-form-item label="培训时间">
            <span class="detail-value">{{ selectedSchedule?.trainStart }} 至 {{ selectedSchedule?.trainEnd }}</span>
          </el-form-item>
          <el-form-item label="授课对象">
            <span class="detail-value">{{ selectedSchedule?.trainObject || '-' }}</span>
          </el-form-item>
          <el-form-item label="培训地点">
            <span class="detail-value">{{ selectedSchedule?.trainAddress || '-' }}</span>
          </el-form-item>
          <el-form-item label="培训需求">
            <span class="detail-value">{{ selectedSchedule?.trainDemand || '-' }}</span>
          </el-form-item>
          <el-form-item label="支付状态">
            <span class="detail-value">{{ getPayStatusText(selectedSchedule?.trainPayStatus) }}</span>
          </el-form-item>
        </el-form>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button type="danger" :loading="deleteLoading" @click="handleDeleteFromDetail">删除</el-button>
        <el-button @click="dialogVisible = false">关闭</el-button>
        <el-button type="primary" @click="handleEdit">编辑</el-button>
      </span>
    </el-dialog>

    <!-- 编辑弹窗 -->
    <el-dialog :title="editDialogType === 'add' ? '新增日程' : '编辑日程'" :visible.sync="editDialogVisible" width="500px" append-to-body class="schedule-dialog">
      <el-form ref="editFormRef" :model="editForm" :rules="editRules" label-width="100px">
        <el-form-item label="讲师">
          <span>{{ selectedSchedule?.teacherName }}</span>
        </el-form-item>
        <el-form-item label="日程类型" prop="atstId">
          <el-select v-model="editForm.atstId" placeholder="请选择" style="width: 100%;">
            <el-option v-for="type in scheduleTypeList" :key="type.atstId" :label="type.atstName" :value="type.atstId">
              <span class="type-dot" :style="{ backgroundColor: type.atstColor }"></span>
              {{ type.atstName }}
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="客户名称" prop="trainCustomerName">
          <el-input v-model="editForm.trainCustomerName" placeholder="请输入" />
        </el-form-item>
        <el-form-item label="课程名称" prop="trainCourseName">
          <el-input v-model="editForm.trainCourseName" placeholder="请输入" />
        </el-form-item>
        <el-form-item label="培训时长">
          <el-input v-model="editForm.trainTimeLength" placeholder="请输入天数" />
        </el-form-item>
        <el-form-item label="培训形式">
          <el-radio-group v-model="editForm.trainForm">
            <el-radio :label="1">线下</el-radio>
            <el-radio :label="2">线上</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="开始时间" prop="trainStart" label-width="100px">
              <el-date-picker v-model="editForm.trainStart" type="date" placeholder="选择时间" style="width: 100%;" value-format="yyyy-MM-dd" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="结束时间" prop="trainEnd" label-width="100px">
              <el-date-picker v-model="editForm.trainEnd" type="date" placeholder="选择时间" style="width: 100%;" value-format="yyyy-MM-dd" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="授课对象">
          <el-input v-model="editForm.trainObject" placeholder="请输入" />
        </el-form-item>
        <el-form-item label="培训地点">
          <el-input v-model="editForm.trainAddress" placeholder="请输入" />
        </el-form-item>
        <el-form-item label="培训需求">
          <el-input v-model="editForm.trainDemand" type="textarea" :rows="3" placeholder="请输入" />
        </el-form-item>
        <el-form-item label="支付状态">
          <el-radio-group v-model="editForm.trainPayStatus">
            <el-radio :label="1">未开票</el-radio>
            <el-radio :label="2">已开票未支付</el-radio>
            <el-radio :label="3">已支付</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button v-if="editDialogType === 'edit'" type="danger" size="small" @click="handleDelete">删除</el-button>
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="editLoading" @click="handleSave">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getSysAjCalendarMonthSchedule, getAjTeacherScheduleMonth, getAjTeacherScheduleTypeList, getAjTeacherScheduleDetails, addAjTeacherSchedule, updateAjTeacherSchedule, deleteAjTeacherSchedule } from "../../api/api";

export default {
  name: "MonthView",
  data() {
    return {
      currentYear: new Date().getFullYear(),
      currentMonth: new Date().getMonth() + 1,
      loading: false,
      weekDays: ['日', '一', '二', '三', '四', '五', '六'],

      // 行程类型
      scheduleTypeList: [],

      // 日历数据
      calendarData: [],
      calendarDays: [],
      sacIdToDayMap: {}, // sacId 到日期的映射

      // 行程数据
      scheduleList: [],

      // 详情弹窗
      dialogVisible: false,
      selectedSchedule: null,
      deleteLoading: false,

      // 编辑弹窗
      editDialogVisible: false,
      editDialogType: 'add',
      editLoading: false,
      editForm: {
        atsId: null,
        teacherId: null,
        atstId: null,
        trainCustomerName: '',
        trainCourseName: '',
        trainTimeLength: '',
        trainStart: '',
        trainEnd: '',
        trainObject: '',
        trainAddress: '',
        trainDemand: '',
        trainForm: 1,
        trainPayStatus: 1
      },
      editRules: {
        atstId: [{ required: true, message: '请选择日程类型', trigger: 'change' }],
        trainCustomerName: [{ required: true, message: '请输入客户名称', trigger: 'blur' }],
        trainCourseName: [{ required: true, message: '请输入课程名称', trigger: 'blur' }],
        trainStart: [{ required: true, message: '请选择开始时间', trigger: 'change' }],
        trainEnd: [{ required: true, message: '请选择结束时间', trigger: 'change' }]
      }
    };
  },
  computed: {
    // 生成月历格子数据
    calendarCells() {
      const cells = [];
      const firstDay = new Date(this.currentYear, this.currentMonth - 1, 1);
      const lastDay = new Date(this.currentYear, this.currentMonth, 0);
      const today = new Date();

      // 获取当月第一天是星期几 (0-6)
      const firstWeekday = firstDay.getDay();
      // 获取当月天数
      const daysInMonth = lastDay.getDate();

      // 填充空白格子（月初前的日子）
      for (let i = 0; i < firstWeekday; i++) {
        cells.push({
          day: 0,
          isOtherMonth: true,
          isWeekend: false,
          isToday: false,
          schedules: []
        });
      }

      // 填充当月日期
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(this.currentYear, this.currentMonth - 1, day);
        const weekday = date.getDay();
        const isWeekend = weekday === 0 || weekday === 6;
        const isToday = today.getFullYear() === this.currentYear &&
          today.getMonth() + 1 === this.currentMonth &&
          today.getDate() === day;

        // 查找该日期的行程
        const schedules = this.getDaySchedules(day);

        cells.push({
          day,
          isOtherMonth: false,
          isWeekend,
          isToday,
          schedules
        });
      }

      // 补全最后一行到7的倍数
      const remainder = cells.length % 7;
      if (remainder !== 0) {
        const emptyCount = 7 - remainder;
        for (let i = 0; i < emptyCount; i++) {
          cells.push({
            day: 0,
            isOtherMonth: true,
            isWeekend: false,
            isToday: false,
            schedules: []
          });
        }
      }

      return cells;
    }
  },
  watch: {
    currentYear: {
      handler() {
        this.fetchMonthData();
      }
    },
    currentMonth: {
      handler() {
        this.fetchMonthData();
      }
    }
  },
  mounted() {
    this.initData();
  },
  methods: {
    async initData() {
      this.fetchMonthData();
      this.fetchScheduleTypeList();
    },

    // 获取月份日历数据
    async fetchMonthData() {
      this.loading = true;
      try {
        const calendarRes = await getSysAjCalendarMonthSchedule({
          sacYearMonth: `${this.currentYear}-${String(this.currentMonth).padStart(2, '0')}`
        });

        if (calendarRes.status === 200 && calendarRes.result === 'success') {
          this.calendarData = calendarRes.data || [];
          this.processCalendarData();
          // 获取日程数据
          await this.fetchMonthSchedule();
        } else {
          this.$message.error(calendarRes.msg || '获取日历数据失败');
        }
      } catch (error) {
        console.error('获取月份数据失败:', error);
      } finally {
        this.loading = false;
      }
    },

    // 处理日历数据
    processCalendarData() {
      this.calendarDays = [];
      this.sacIdToDayMap = {};

      if (this.calendarData && this.calendarData.sysAjCalendarChildren) {
        const children = this.calendarData.sysAjCalendarChildren;
        const days = children
          .filter(item => item.sacType === 0)
          .sort((a, b) => (a.sacDay || 0) - (b.sacDay || 0));

        this.calendarDays = days;

        // 建立sacId到日期的映射
        days.forEach(day => {
          this.sacIdToDayMap[day.sacId] = day.sacDay;
        });
      }
    },

    // 获取月份日程数据
    async fetchMonthSchedule() {
      try {
        const res = await getAjTeacherScheduleMonth({
          sacYearMonth: `${this.currentYear}-${String(this.currentMonth).padStart(2, '0')}`
        });

        if (res.status === 200 && res.data) {
          this.scheduleList = res.data || [];
        } else {
          console.error('获取日程数据失败:', res.msg);
        }
      } catch (error) {
        console.error('获取日程数据失败:', error);
      }
    },

    // 获取某天的行程
    getDaySchedules(day) {
      if (!this.scheduleList.length) return [];

      const schedules = [];

      // 构造当前日期字符串 YYYY-MM-DD
      const currentDateStr = `${this.currentYear}-${String(this.currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

      this.scheduleList.forEach(schedule => {
        // 直接比较日期字符串，确保包含开始和结束日期
        if (currentDateStr >= schedule.trainStart && currentDateStr <= schedule.trainEnd) {
          // 获取对应的atstColor
          const typeInfo = this.scheduleTypeList.find(t => t.atstId === schedule.atstId);
          const displayColor = schedule.atstColor || typeInfo?.atstColor || '#409eff';

          // 获取行程类型名称
          const atstName = typeInfo?.atstName || '';

          // 每一天都显示文本：讲师名-客户名
          const displayText = `${schedule.teacherName}-${schedule.trainCustomerName}`;

          schedules.push({
            ...schedule,
            displayText,
            displayColor,
            atstName,
            currentDay: day
          });
        }
      });

      return schedules;
    },

    // 获取行程类型列表
    async fetchScheduleTypeList() {
      try {
        const res = await getAjTeacherScheduleTypeList();
        if (res.status === 200) {
          this.scheduleTypeList = res.data || [];
        } else {
          this.$message.error(res.msg || '获取行程类型失败');
        }
      } catch (error) {
        console.error('获取行程类型失败:', error);
      }
    },

    // 点击行程
    handleScheduleClick(schedule) {
      this.selectedSchedule = schedule;
      this.dialogVisible = true;
    },

    // 获取支付状态文本
    getPayStatusText(status) {
      const statusMap = { 1: '未开票', 2: '已开票未支付', 3: '已支付' };
      return statusMap[status] || '-';
    },

    // 获取头像文字
    getAvatarText(name) {
      if (!name) return '讲师';
      const len = name.length;
      if (len <= 2) return name;
      return name.substring(len - 2);
    },

    // 编辑
    handleEdit() {
      this.editDialogType = 'edit';
      this.editForm = {
        atsId: this.selectedSchedule.atsId,
        teacherId: this.selectedSchedule.teacherId,
        atstId: this.selectedSchedule.atstId,
        trainCustomerName: this.selectedSchedule.trainCustomerName,
        trainCourseName: this.selectedSchedule.trainCourseName,
        trainTimeLength: this.selectedSchedule.trainTimeLength,
        trainStart: this.selectedSchedule.trainStart,
        trainEnd: this.selectedSchedule.trainEnd,
        trainObject: this.selectedSchedule.trainObject,
        trainAddress: this.selectedSchedule.trainAddress,
        trainDemand: this.selectedSchedule.trainDemand,
        trainForm: this.selectedSchedule.trainForm,
        trainPayStatus: this.selectedSchedule.trainPayStatus
      };
      this.dialogVisible = false;
      this.editDialogVisible = true;
    },

    // 从详情弹窗删除日程
    async handleDeleteFromDetail() {
      if (!this.selectedSchedule?.atsId) return;

      try {
        await this.$confirm('确定要删除该日程吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });

        this.deleteLoading = true;
        const res = await deleteAjTeacherSchedule({ atsId: this.selectedSchedule.atsId });

        if (res.status === 200) {
          this.$message.success('删除成功');
          this.dialogVisible = false;
          this.fetchMonthSchedule();
        } else {
          this.$message.error(res.msg || '删除失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error);
          this.$message.error('删除失败');
        }
      } finally {
        this.deleteLoading = false;
      }
    },

    // 保存
    async handleSave() {
      if (!this.$refs.editFormRef) return;

      try {
        await this.$refs.editFormRef.validate();

        this.editLoading = true;
        const params = { ...this.editForm };

        let res;
        if (this.editDialogType === 'edit') {
          res = await updateAjTeacherSchedule(params);
        } else {
          res = await addAjTeacherSchedule(params);
        }

        if (res.status === 200) {
          this.$message.success(this.editDialogType === 'edit' ? '保存成功' : '新增成功');
          this.editDialogVisible = false;
          this.fetchMonthSchedule();
        } else {
          this.$message.error(res.msg || '操作失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('保存失败:', error);
        }
      } finally {
        this.editLoading = false;
      }
    },

    // 删除
    async handleDelete() {
      if (!this.editForm.atsId) return;

      try {
        await this.$confirm('确定要删除该日程吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });

        const res = await deleteAjTeacherSchedule({ atsId: this.editForm.atsId });

        if (res.status === 200) {
          this.$message.success('删除成功');
          this.editDialogVisible = false;
          this.fetchMonthSchedule();
        } else {
          this.$message.error(res.msg || '删除失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error);
        }
      }
    },

    // 切换年份
    changeYear(delta) {
      this.currentYear += delta;
    },

    // 切换月份
    handleMonthChange() {
      // 月份变化会自动触发 watch
    }
  }
};
</script>

<style scoped>
.month-view-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
  padding: 16px 20px;
  overflow: hidden;
}

/* 顶部工具栏 */
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  margin-bottom: 16px;
  flex-shrink: 0;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 24px;
}

.toolbar-title {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  position: relative;
  padding-left: 14px;
}

.toolbar-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 20px;
  background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
  border-radius: 2px;
}

.year-picker {
  display: flex;
  align-items: center;
  gap: 0;
  background: #f5f7fa;
  border-radius: 8px;
  padding: 4px;
}

.year-picker-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #909399;
  border-radius: 6px;
  transition: all 0.2s;
  font-size: 16px;
}

.year-picker-btn:hover {
  background: #e4e7ed;
  color: #606266;
}

.year-picker-btn:active {
  transform: scale(0.95);
}

.year-value {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  min-width: 60px;
  text-align: center;
  padding: 0 12px;
}

.month-picker {
  width: 100px;
}

.month-picker ::v-deep .el-input__inner {
  border: none;
  background: #f5f7fa;
  color: #303133;
  font-weight: 600;
  font-size: 18px;
  height: 44px;
  border-radius: 8px;
  padding-left: 12px;
}

.month-picker ::v-deep .el-input__icon {
  line-height: 44px;
  color: #909399;
  font-size: 14px;
}

.month-picker ::v-deep .el-input__inner::placeholder {
  color: #c0c4cc;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

.toolbar-legend {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 8px 16px;
  background: #fafbfc;
  border-radius: 10px;
  border: 1px solid #ebeef5;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.legend-item:hover {
  transform: translateY(-2px);
}

.legend-color-block {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.legend-text {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
}

/* 月历视图 */
.calendar-view {
  flex: 1;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* 星期表头 */
.week-header {
  display: flex;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 0;
  flex-shrink: 0;
}

.week-cell {
  flex: 1;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  color: #fff;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
}

.week-cell:last-child {
  border-right: none;
}

.week-cell.is-weekend {
  background: rgba(255, 255, 255, 0.1);
}

/* 日期格子 */
.calendar-grid {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-auto-rows: minmax(80px, 1fr);
  background: #f5f7fa;
  overflow: auto;
}

.calendar-cell {
  padding: 6px 8px;
  border-right: 1px solid #ebeef5;
  border-bottom: 1px solid #ebeef5;
  box-sizing: border-box;
  background: #fff;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 80px;
}

.calendar-cell:nth-child(7n) {
  border-right: none;
}

.calendar-cell.is-empty {
  background: #fafbfc;
}

.calendar-cell.is-weekend {
  background: #f8fafc;
}

.calendar-cell.is-today {
  background: linear-gradient(135deg, #ecf5ff 0%, #e6f2ff 100%);
  box-shadow: inset 0 0 0 2px #409eff;
}

.calendar-cell.is-other-month {
  opacity: 0.4;
}

.calendar-cell:hover:not(.is-empty) {
  background: linear-gradient(135deg, #f0f7ff 0%, #e6f4ff 100%);
  z-index: 1;
}

.cell-content {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.cell-content .day-number {
  flex-shrink: 0;
  margin-bottom: 4px;
}

.schedule-tags {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 20px;
  max-height: 100%;
  padding-right: 4px;
  scrollbar-width: thin;
  scrollbar-color: #667eea transparent;
}

/* 自定义滚动条样式 - Webkit */
.schedule-tags::-webkit-scrollbar {
  width: 5px;
}

.schedule-tags::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 3px;
}

.schedule-tags::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
  border-radius: 3px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

.schedule-tags::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #5a6fd6 0%, #6a4190 100%);
  box-shadow: 0 2px 5px rgba(102, 126, 234, 0.4);
}

.schedule-tags::-webkit-scrollbar-thumb:active {
  background: linear-gradient(180deg, #4a5fc6 0%, #5a3680 100%);
}

.day-number {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 6px;
}

.is-weekend .day-number {
  color: #909399;
}

.is-today .day-number {
  color: #409eff;
}

.schedule-tag {
  font-size: 11px;
  color: #303133;
  padding: 3px 6px;
  border-radius: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}

.schedule-tag:hover {
  transform: translateX(2px);
}

/* 弹窗样式 */
.schedule-dialog ::v-deep .el-dialog {
  border-radius: 12px;
}

.schedule-dialog ::v-deep .el-dialog__header {
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.schedule-dialog ::v-deep .el-dialog__title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.schedule-dialog ::v-deep .el-dialog__body {
  padding: 20px;
}

.schedule-dialog ::v-deep .el-dialog__footer {
  padding: 12px 20px;
  border-top: 1px solid #f0f0f0;
}

.dialog-content {
  padding: 0;
}

.dialog-header-mini {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 8px;
  margin-bottom: 16px;
}

.lecturer-avatar-mini {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
}

.lecturer-info-mini {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.lecturer-name-mini {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.schedule-type-tag {
  display: inline-block;
  font-size: 11px;
  color: #fff;
  padding: 2px 8px;
  border-radius: 3px;
  width: fit-content;
}

.detail-form ::v-deep .el-form-item {
  margin-bottom: 12px;
}

.detail-form ::v-deep .el-form-item__label {
  font-size: 13px;
  color: #909399;
  padding-right: 8px;
}

.detail-value {
  font-size: 13px;
  color: #303133;
}

.type-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
}

/* 加载状态 */
.month-view-container ::v-deep .el-loading-mask {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
}

.month-view-container ::v-deep .el-loading-spinner .el-loading-text {
  color: #667eea;
  font-weight: 500;
}

.month-view-container ::v-deep .el-loading-spinner .path {
  stroke: #667eea;
}
</style>
